import React, {useState, useEffect} from "react";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@relume_io/relume-ui";
import {AnimatePresence, motion} from "framer-motion";

const currentYear = new Date().getFullYear();

const GoogleCalendarTabsDefaults = {
    tagline: `Skate into ${currentYear}: Registration Open!`,
    heading: `${currentYear} Schedule`,
    description:
        "Registration is open - use the Register tab in the top right corner! The schedule is below for PreCanSkate and CanSkate under Learn to Skate",
    defaultValue: "tab-1",
    tabs: [
        {
            value: "tab-1",
            trigger: "StarSkate",
            content: {
                iframeSrc:
                    "https://calendar.google.com/calendar/embed?src=federationskating%40gmail.com&ctz=America%2FEdmonton",
                iframeTitle: "Calendar 1",
                ical:
                    "https://calendar.google.com/calendar/ical/federationskating%40gmail.com/public/basic.ics",
                category: "StarSkate",
            },
        },
        {
            value: "tab-2",
            trigger: "Learn To Skate",
            content: {
                iframeSrc:
                    "https://calendar.google.com/calendar/embed?src=a22668c2091cb38b7053b4db1a64bb3d3e993a0e9f6080beffb9abaae835cf85%40group.calendar.google.com&ctz=America%2FEdmonton",
                iframeTitle: "Calendar 2",
                ical:
                    "https://calendar.google.com/calendar/ical/a22668c2091cb38b7053b4db1a64bb3d3e993a0e9f6080beffb9abaae835cf85%40group.calendar.google.com/public/basic.ics",
                category: "Learn To Skate",
            },
        },
        {
            value: "tab-3",
            trigger: "Intro to Figure Skating",
            content: {
                iframeSrc:
                    "https://calendar.google.com/calendar/embed?src=0c4f1b78e382d2c7b92d84ea6c9e70fd2faa4f2957bde7d71594897feedb642e%40group.calendar.google.com&ctz=America%2FEdmonton",
                iframeTitle: "Calendar 3",
                ical:
                    "https://calendar.google.com/calendar/ical/0c4f1b78e382d2c7b92d84ea6c9e70fd2faa4f2957bde7d71594897feedb642e%40group.calendar.google.com/public/basic.ics",
                category: "Intro to Figure Skating",
            },
        },
        {
            value: "tab-4",
            trigger: "PrePower",
            content: {
                iframeSrc:
                    "https://calendar.google.com/calendar/embed?src=78fb86290132d6eebf5420b417b0ed635e9bc25a45700635dd7543cf2102b2f1%40group.calendar.google.com&ctz=America%2FEdmonton",
                iframeTitle: "Calendar 4",
                ical:
                    "https://calendar.google.com/calendar/ical/78fb86290132d6eebf5420b417b0ed635e9bc25a45700635dd7543cf2102b2f1%40group.calendar.google.com/public/basic.ics",
                category: "PrePower",
            },
        },
    ],
};

const GoogleCalendarTabs = (props) => {
    const { tagline, heading, description, defaultValue, tabs } = {
        ...GoogleCalendarTabsDefaults,
        ...props,
    };

    const [activeTab, setActiveTab] = useState(defaultValue);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Slide animation variants
    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 300 : -300,
            opacity: 0,
            position: "absolute",
            width: "100%",
            top: 0,
            left: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
            position: "relative",
            width: "100%",
            top: 0,
            left: 0,
        },
        exit: (direction) => ({
            x: direction > 0 ? -300 : 300,
            opacity: 0,
            position: "absolute",
            width: "100%",
            top: 0,
            left: 0,
        }),
    };

    // Track direction to slide left or right
    const [direction, setDirection] = useState(0);

    const handleTabChange = (value) => {
        const currentIndex = tabs.findIndex((tab) => tab.value === activeTab);
        const newIndex = tabs.findIndex((tab) => tab.value === value);
        setDirection(newIndex > currentIndex ? 1 : -1);
        setActiveTab(value);
    };

    return (
        <section className="px-[5%] py-16 md:py-24 lg:py-28 bg-gray-50">
            <div className="container max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-16 text-center max-w-3xl mx-auto">
                    {tagline && (
                        <p className="mb-3 text-sm font-semibold uppercase text-red-600 tracking-widest">
                            {tagline}
                        </p>
                    )}
                    <h1 className="mb-5 text-4xl font-extrabold text-gray-900 md:text-6xl lg:text-7xl">
                        {heading}
                    </h1>
                    <p className="text-lg text-gray-700">{description}</p>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    {/* Centered and horizontally scrollable tab triggers */}
                    <div className="flex flex-col md:flex-row justify-center items-stretch md:items-center gap-4 md:gap-0 mb-12">
                        <TabsList className="flex flex-col md:flex-row md:space-x-4 w-full md:w-auto space-y-2 md:space-y-0">
                        {tabs.map((tab, index) => (
                                <TabsTrigger
                                    key={index}
                                    value={tab.value}
                                    className={`px-6 py-3 text-lg font-bold rounded-t-md cursor-pointer
                    ${
                                        activeTab === tab.value
                                            ? "bg-red-500 text-white"
                                            : "bg-background-primary text-text-primary hover:bg-red-100"
                                    }`}
                                >
                                    {tab.trigger}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>

                    {/* All tab contents rendered and animated with slide */}
                    <div className="relative w-full max-w-5xl mx-auto rounded-lg shadow-md overflow-hidden min-h-[600px] md:min-h-[700px]">
                        {tabs.map((tab, index) => (
                            <motion.div
                                key={tab.value}
                                custom={direction}
                                variants={variants}
                                initial="enter"
                                animate={activeTab === tab.value ? "center" : "exit"}
                                exit="exit"
                                transition={{ duration: 0.4, ease: "easeInOut" }}
                                style={{
                                    position: activeTab === tab.value ? "relative" : "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    pointerEvents: activeTab === tab.value ? "auto" : "none", // <-- add this line
                                }}
                                aria-hidden={activeTab !== tab.value}
                            >
                                <iframe
                                    src={isMobile ? `${tab.content.iframeSrc}&mode=AGENDA` : tab.content.iframeSrc}
                                    title={tab.content.iframeTitle}
                                    allowFullScreen
                                    className="min-h-[600px] md:min-h-[700px]  max-w-5xl w-full border-0 rounded-lg"
                                    tabIndex={activeTab === tab.value ? 0 : -1}
                                />
                                {tab.content.ical && (
                                    <p className="mt-2 md:text-md text-center">
                                        Download the {tab.content.category}{" "}
                                        <a
                                            href={tab.content.ical}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-red-600 underline"
                                        >
                                            iCal
                                        </a>{" "}
                                        link.
                                    </p>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </Tabs>
            </div>
        </section>
    );
};

export default GoogleCalendarTabs;

